import {
  Alert,
  Button,
  FileButton,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import React, { useCallback, useMemo, useState } from 'react';
import { Check, ExclamationMark, Upload } from 'tabler-icons-react';

import { useCreateIssue } from 'api/issues/createIssue';
import { useGetProjects } from 'api/projects/getProjects';
import { useUser } from 'hooks/useUser';
import { Issue } from 'types';

type BulkCreateIssuesProps = {
  opened: boolean;
  onClose: () => void;
  defaultProjectId?: string;
};

type CSVRow = {
  title: string;
  description?: string;
  type?: Issue['type'];
  priority?: Issue['priority'];
  assignee?: string;
  labels?: string;
  components?: string;
  fixVersions?: string;
  affectsVersions?: string;
  environment?: 'development' | 'staging' | 'production';
  dueDate?: string;
  errors?: string[];
};

function BulkCreateIssues({
  opened,
  onClose,
  defaultProjectId,
}: BulkCreateIssuesProps) {
  const userContext = useUser();
  const user = userContext?.user;
  const orgId = user?.org?._id;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { data: projectsData } = useGetProjects(orgId);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    defaultProjectId || ''
  );
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  const createIssueMutation = useCreateIssue(orgId, () => {
    // Success handled per issue
  });

  const projectOptions = useMemo(
    () =>
      projectsData?.projects.map((p) => ({
        value: p._id,
        label: p.name,
      })) || [],
    [projectsData]
  );

  const parseCSV = useCallback((csvText: string): CSVRow[] => {
    const lines = csvText.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0]
      .split(',')
      .map((h) => h.trim().replace(/^"|"$/g, ''));
    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i]
        .split(',')
        .map((v) => v.trim().replace(/^"|"$/g, ''));
      const row: CSVRow = { title: '' };
      const errors: string[] = [];

      headers.forEach((header, index) => {
        const value = values[index] || '';
        const normalizedHeader = header.toLowerCase().trim();

        switch (normalizedHeader) {
          case 'title':
          case 'name':
          case 'summary':
            if (!value) {
              errors.push('Title is required');
            }
            row.title = value;
            break;
          case 'description':
          case 'desc':
            row.description = value;
            break;
          case 'type':
          case 'issuetype':
            if (
              ['task', 'story', 'bug', 'epic', 'sub-task'].includes(
                value.toLowerCase()
              )
            ) {
              row.type = value.toLowerCase() as Issue['type'];
            } else if (value) {
              errors.push(`Invalid type: ${value}`);
            }
            break;
          case 'priority':
            if (['low', 'medium', 'high'].includes(value.toLowerCase())) {
              row.priority = value.toLowerCase() as Issue['priority'];
            } else if (value) {
              errors.push(`Invalid priority: ${value}`);
            }
            break;
          case 'assignee':
          case 'assigneeemail':
            row.assignee = value;
            break;
          case 'labels':
            row.labels = value;
            break;
          case 'components':
            row.components = value;
            break;
          case 'fixversions':
          case 'fix versions':
            row.fixVersions = value;
            break;
          case 'affectsversions':
          case 'affects versions':
            row.affectsVersions = value;
            break;
          case 'environment':
            if (
              ['development', 'staging', 'production'].includes(
                value.toLowerCase()
              )
            ) {
              row.environment = value.toLowerCase() as
                | 'development'
                | 'staging'
                | 'production';
            } else if (value) {
              errors.push(`Invalid environment: ${value}`);
            }
            break;
          case 'duedate':
          case 'due date':
            row.dueDate = value;
            break;
          default:
            // Unknown header, skip
            break;
        }
      });

      if (errors.length > 0) {
        row.errors = errors;
      }

      if (row.title) {
        rows.push(row);
      }
    }

    return rows;
  }, []);

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        const text = await file.text();
        const parsed = parseCSV(text);
        setCsvData(parsed);
        setProcessedCount(0);
        setErrorCount(0);
        showNotification({
          title: 'CSV Parsed',
          message: `Found ${parsed.length} issues to create`,
          color: 'teal',
          icon: <Check />,
        });
      } catch (error) {
        showNotification({
          title: 'Error parsing CSV',
          message:
            error instanceof Error ? error.message : 'Invalid CSV format',
          color: 'red',
          icon: <ExclamationMark />,
        });
      }
    },
    [parseCSV]
  );

  const handleBulkCreate = useCallback(async () => {
    if (!selectedProjectId || csvData.length === 0 || !user?._id) {
      return;
    }

    setIsProcessing(true);
    setProcessedCount(0);
    setErrorCount(0);

    let successCount = 0;
    let failCount = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const row of csvData) {
      if (row.errors && row.errors.length > 0) {
        failCount++;
        // eslint-disable-next-line no-continue
        continue;
      }

      try {
        const issueData: Parameters<typeof createIssueMutation.mutate>[0] = {
          project: selectedProjectId,
          title: row.title,
          description: row.description,
          type: row.type || 'task',
          priority: row.priority || 'medium',
          status: 'to do',
          assignee: row.assignee,
          labels: row.labels
            ? row.labels.split(',').map((l) => l.trim()).filter(Boolean)
            : undefined,
          components: row.components
            ? row.components.split(',').map((c) => c.trim()).filter(Boolean)
            : undefined,
          fixVersions: row.fixVersions
            ? row.fixVersions.split(',').map((v) => v.trim()).filter(Boolean)
            : undefined,
          affectsVersions: row.affectsVersions
            ? row.affectsVersions.split(',').map((v) => v.trim()).filter(Boolean)
            : undefined,
          environment: row.environment,
          dueDate: (() => {
            if (row.dueDate) {
              const d = new Date(row.dueDate);
              if (!Number.isNaN(d.getTime())) return d.toISOString();
            }
            const defaultDue = new Date();
            defaultDue.setDate(defaultDue.getDate() + 30);
            return defaultDue.toISOString();
          })(),
          createdBy: user._id,
        };

        // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-loop-func
        await new Promise<void>((resolve) => {
          createIssueMutation.mutate(issueData, {
            onSuccess: () => {
              successCount++;
              setProcessedCount(successCount + failCount);
              resolve();
            },
            onError: (error) => {
              failCount++;
              setErrorCount(failCount);
              setProcessedCount(successCount + failCount);
              console.error('Failed to create issue:', error);
              resolve(); // Continue with next issue
            },
          });
        });

        // Small delay to avoid overwhelming the server
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 100);
        });
      } catch (error) {
        failCount++;
        setErrorCount(failCount);
        setProcessedCount(successCount + failCount);
        console.error('Error creating issue:', error);
      }
    }

    setIsProcessing(false);

    showNotification({
      title: 'Bulk Create Complete',
      message: `Created ${successCount} issues${failCount > 0 ? `, ${failCount} failed` : ''}`,
      color: failCount > 0 ? 'yellow' : 'teal',
      icon: failCount > 0 ? <ExclamationMark /> : <Check />,
    });

    if (successCount > 0) {
      setCsvData([]);
      onClose();
    }
  }, [selectedProjectId, csvData, user, createIssueMutation, onClose]);

  const handleClose = useCallback(() => {
    setCsvData([]);
    setProcessedCount(0);
    setErrorCount(0);
    setIsProcessing(false);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Bulk Create Issues from CSV"
      size={isMobile ? 'full' : 'xl'}
      centered
    >
      <Stack spacing="md">
        <Alert icon={<Upload size={16} />} color="blue" title="CSV Format">
          <Text size="sm">
            Your CSV should have a header row with columns: Title, Description,
            Type, Priority, Assignee, Labels, Components, Fix Versions, Affects
            Versions, Environment, Due Date
          </Text>
          <Text size="xs" color="dimmed" mt="xs">
            Required: Title. Due Date is required by the server; include a
            &quot;Due Date&quot; column (e.g. YYYY-MM-DD) or leave blank to use
            30 days from today. Type: task, story, bug, epic, sub-task. Priority:
            low, medium, high.
          </Text>
        </Alert>

        <Group>
          <FileButton
            onChange={handleFileUpload}
            accept=".csv"
            disabled={isProcessing}
          >
            {(props) => (
              <Button
                {...props}
                leftIcon={<Upload size={16} />}
                variant="light"
              >
                Upload CSV File
              </Button>
            )}
          </FileButton>
          {csvData.length > 0 && (
            <Text size="sm" color="dimmed">
              {csvData.length}
              {' '}
              issues ready to create
            </Text>
          )}
        </Group>

        {csvData.length > 0 && (
          <>
            <Paper withBorder p="md" radius="md">
              <Stack spacing="md">
                <Group position="apart">
                  <Text size="sm" weight={600}>
                    Preview (
                    {csvData.length}
                    {' '}
                    issues)
                  </Text>
                  {isProcessing && (
                    <Text size="sm" color="blue">
                      Processing:
                      {' '}
                      {processedCount}
                      /
                      {csvData.length}
                      {' '}
                      (
                      {errorCount}
                      {' '}
                      errors)
                    </Text>
                  )}
                </Group>

                <ScrollArea style={{ maxHeight: 400 }}>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Priority</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row) => {
                        const rowKey = `row-${row.title || 'untitled'}-${row.type || 'task'}-${row.priority || 'medium'}-${row.assignee || 'unassigned'}`;
                        return (
                          <tr key={rowKey}>
                            <td>
                              <div>
                                <Text size="sm" weight={500}>
                                  {row.title}
                                </Text>
                                {row.errors && row.errors.length > 0 && (
                                  <Group spacing={4} mt={4}>
                                    {row.errors.map((error) => (
                                      <Text
                                        key={`error-${rowKey}-${error}`}
                                        size="xs"
                                        color="red"
                                        component="span"
                                      >
                                        •
                                        {' '}
                                        {error}
                                      </Text>
                                    ))}
                                  </Group>
                                )}
                              </div>
                            </td>
                            <td>
                              <Text size="sm">{row.type || 'task'}</Text>
                            </td>
                            <td>
                              <Text size="sm">{row.priority || 'medium'}</Text>
                            </td>
                            <td>
                              <Text size="sm">to do</Text>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </ScrollArea>
              </Stack>
            </Paper>

            <Group position="apart">
              <Select
                label="Project"
                placeholder="Select project"
                data={projectOptions}
                value={selectedProjectId}
                onChange={(value) => setSelectedProjectId(value || '')}
                required
                disabled={isProcessing || !!defaultProjectId}
                searchable
                style={{ flex: 1 }}
              />
              <Group>
                <Button
                  variant="subtle"
                  onClick={handleClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkCreate}
                  loading={isProcessing}
                  disabled={!selectedProjectId || csvData.length === 0}
                  leftIcon={<Check size={16} />}
                >
                  {isProcessing
                    ? `Creating... (${processedCount}/${csvData.length})`
                    : `Create ${csvData.length} Issues`}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default BulkCreateIssues;
